import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FileInfo } from '../Types';
import { extractExifData } from './useExifData';
import { getPresignedUrl, putFile, saveImage, checkProcessingStatus } from 'src/apiCalls';
import { UPLOAD_CONSTANTS } from '../Constants';
import logger from 'src/logger';
import { ExifData } from '../Types';
const initialFileState: FileInfo = {
    publicizeLocation: false,
    chargeForLocation: false,
    freeForRegisteredUsers: false,
    freeForEveryone: true, // Default to free for everyone
    file: null,
    hasExif: false,
    latitude: null,
    longitude: null,
    area: '',
    exif: null,
    id: '',
    processingComplete: false,
    tags: '',
    species: '',
    weight: undefined,
    price: undefined
  };

export const useFileUpload = (user: any) => {
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFileUpload = useCallback(async (file: File | null) => {
    if (!file) {
      setFileInfo(null);
      setErrorMessage('');
      return;
    }

    const isValidFile = file.name.toLowerCase().endsWith('.jpg') || 
                       file.name.toLowerCase().endsWith('.jpeg');

    if (!isValidFile) {
      setErrorMessage('Please select only JPG/JPEG images');
      return;
    }

    try {
      const exifData: ExifData = await extractExifData(file);
      
      setFileInfo({
        ...initialFileState,
        file,
        hasExif: exifData.hasExif,
        latitude: exifData.latitude,
        longitude: exifData.longitude,
        area: exifData.area,
        exif: exifData.exif,
        dateTaken: exifData.dateTaken,
        id: uuidv4()
      });
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Error processing file: ' + (error as Error).message);
    }
  }, []);

  const updateFileInfo = useCallback((updates: Partial<FileInfo>) => {
    setFileInfo(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const checkProcessingComplete = useCallback(async (fileId: string): Promise<boolean> => {
    let attempts = 0;
    while (attempts < UPLOAD_CONSTANTS.MAX_PROCESSING_ATTEMPTS) {
      try {
        const status = await checkProcessingStatus(user.accessToken, user.uid, fileId);
        logger.log('the status', status);
        if (status === 'processed') {
          return true;
        }
  
        if (status === 'unsuitable') {
          throw new Error('The uploaded image was deemed unsuitable.');
        }
  
        if (status === 'notfish') {
          throw new Error('The uploaded image does not appear to contain any fish.');
        }
  
        await new Promise(resolve => 
          setTimeout(resolve, UPLOAD_CONSTANTS.PROCESSING_RETRY_DELAY)
        );
        attempts++;
      } catch (error) {
        logger.error(`Processing status check failed for ${fileId}:`, error);
        throw error; // Re-throw to let the calling function handle it
      }
    }
    throw new Error('File processing timed out.');
  }, [user]);

  const uploadFile = useCallback(async () => {
    if (!fileInfo) {
      logger.error('No file info available');
      return;
    }

    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', fileInfo.file);
      formData.append('id', fileInfo.id);
      formData.append('tags', JSON.stringify(fileInfo.tags || []));
      formData.append('price', fileInfo.price?.toString() || '0');
      formData.append('species', fileInfo.species || '');
      formData.append('weight', fileInfo.weight?.toString() || '');
      formData.append('latitude', fileInfo.latitude?.toString() || '');
      formData.append('longitude', fileInfo.longitude?.toString() || '');
      formData.append('dateTaken', fileInfo.dateTaken ? new Date(fileInfo.dateTaken).toISOString() : '');
      formData.append('area', fileInfo.area || '');
      formData.append('publicizeLocation', fileInfo.publicizeLocation?.toString() || 'false');
      formData.append('chargeForLocation', fileInfo.chargeForLocation?.toString() || 'false');
      formData.append('freeForRegisteredUsers', fileInfo.freeForRegisteredUsers?.toString() || 'false');
      formData.append('freeForEveryone', fileInfo.freeForEveryone?.toString() || 'true');
      formData.append('minimumPhotoUploads', fileInfo.minimumPhotoUploads?.toString() || '');

      // Step 1: Get presigned URL and upload file
      const presignedUrlResponse = await getPresignedUrl(
        user.accessToken,
        user.uid,
        fileInfo.file.name,
        fileInfo.id
      );

      await putFile(
        presignedUrlResponse.signedurl,
        presignedUrlResponse.headers,
        fileInfo.file
      );

      // Step 2: Save image metadata
      const body = {
        id: fileInfo.id,
        tags: fileInfo.tags,
        price: fileInfo.price,
        species: fileInfo.species,
        weight: fileInfo.weight,
        user: user.uid,
        latitude: fileInfo.latitude || 0,
        longitude: fileInfo.longitude || 0,
        uploaded: Date.now(),
        dateTaken: fileInfo.dateTaken || '',
        area: fileInfo.area,
        publicise: fileInfo.publicizeLocation,
        chargeForLocation: fileInfo.chargeForLocation,
        freeForRegisteredUsers: fileInfo.freeForRegisteredUsers,
        freeForEveryone: fileInfo.freeForEveryone,
        minimumPhotoUploads: fileInfo.minimumPhotoUploads,
        status: 'processing',
        path: `https://${process.env.IMAGE_VIEWER_BUCKET}.s3.eu-west-2.amazonaws.com/${fileInfo.file.name}`
      };

      await saveImage(user.accessToken, user.uid, fileInfo.id, body);

      // Step 3: Wait for processing to complete
      await checkProcessingComplete(fileInfo.id);

      return true;
    } catch (error) {
      logger.error('Error:', error);
      setErrorMessage('Upload failed: ' + (error as Error).message);
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [fileInfo, user, checkProcessingComplete]);

  const clearFile = useCallback(() => {
    setFileInfo(null);
    setErrorMessage('');
  }, []);

  const hasValidationErrors = !fileInfo?.file || !fileInfo.tags;
  const isSubmitDisabled = hasValidationErrors || isProcessing;

  const updateField = (field: keyof FileInfo, value: any) => {
    if (!fileInfo) {
      logger.error('No file info available');
      return;
    }
    logger.debug(`updateField called: ${field} = ${value} (type: ${typeof value})`);
    setFileInfo(prev => {
      const newState = {
        ...prev,
        [field]: value
      };
      logger.debug(`New fileInfo state:`, newState);
      return newState;
    });
  };

  return {
    fileInfo,
    errorMessage,
    isProcessing,
    handleFileUpload,
    updateFileInfo,
    uploadFile,
    clearFile,
    isSubmitDisabled,
    hasValidationErrors,
    updateField
  };
};